import { useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { Box, Typography } from "@mui/material";

import { ortexColorState, themeState } from "@/lib/store";
import { nFormatter } from "@/lib/utils";
import { LockUpWeek } from "@/controllers/lock-ups";

/**
 * Expiries per week for the next quarter, as a column strip.
 *
 * The page's first question is "when". A grid sorted by date answers it one row
 * at a time; twelve columns answer it at a glance, and the shape is the point —
 * lock-up supply arrives in clusters (a busy IPO window ninety days ago is a busy
 * expiry week now), and a cluster is invisible in a list.
 *
 * **One measure on the axis.** The columns count expiries and nothing else. Value
 * unlocked is the more interesting number but only about half of CIQ's headlines
 * carry a share count, so a value axis would draw a quiet week wherever the feed
 * happened to be vague — a chart lying about the data's coverage rather than about
 * the market. Value rides in the tooltip, where its absence reads as absent
 * instead of as zero, and the totals above the strip carry it properly.
 */

interface Props {
    weeks: LockUpWeek[];
    /** Loading placeholder: keep the strip's footprint, draw no bars or labels. */
    ghost?: boolean;
}

/** Bars are capped rather than filling their slot; the leftover is deliberate air. */
const MAX_BAR_WIDTH = 22;

/** Plot height, in px, excluding the axis band below it. */
const PLOT_HEIGHT = 96;

const weekLabel = (iso: string): string => {
    const date = new Date(`${iso}T00:00:00Z`);
    return date.toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });
};

/**
 * Which week columns get an x-axis label.
 *
 * Twelve labels under a strip this wide collide, and a collided axis is worse than
 * a sparse one. Every third week, always including the first, gives a reader
 * enough to locate any column by counting.
 */
export const labelledWeeks = (count: number): Set<number> => {
    const indices = new Set<number>();
    for (let index = 0; index < count; index += 3) indices.add(index);
    return indices;
};

const LockUpWeeks = ({ weeks, ghost }: Props) => {
    const theme = useAtomValue(themeState);
    const ortexColor = useAtomValue(ortexColorState);
    const [hovered, setHovered] = useState<number | null>(null);

    const isDark = theme === "dark";
    const axisColor = isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.14)";

    const { max, busiestIndex } = useMemo(() => {
        let highest = 0;
        let index = -1;
        (weeks ?? []).forEach((week, position) => {
            if (week.count > highest) {
                highest = week.count;
                index = position;
            }
        });
        return { max: highest, busiestIndex: index };
    }, [weeks]);

    if (!weeks?.length || !max) return null;

    const labelled = labelledWeeks(weeks.length);

    return (
        <Box>
            <Typography variant="subtitle2" component="h2" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
                Expiries per week
            </Typography>

            <Box sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: PLOT_HEIGHT, position: "relative", visibility: ghost ? "hidden" : undefined }}>
                {weeks.map((week, index) => {
                    const height = Math.max((week.count / max) * PLOT_HEIGHT, week.count ? 2 : 0);
                    // The first column is the current week. Emphasis is opacity, not a
                    // second hue: a status colour here would compete with the ORTEX
                    // accent for meaning it does not have.
                    const isCurrent = index === 0;
                    const active = hovered === index;
                    return (
                        <Box
                            key={week.weekStart}
                            onMouseEnter={() => setHovered(index)}
                            onMouseLeave={() => setHovered(null)}
                            sx={{
                                flex: 1,
                                maxWidth: MAX_BAR_WIDTH,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-end",
                                // The hit target is the whole column, not the drawn bar —
                                // a two-expiry week is 2px tall and otherwise unhoverable.
                                cursor: "default",
                            }}
                        >
                            {index === busiestIndex && (
                                <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center", lineHeight: 1, marginBottom: "3px" }}>
                                    {week.count}
                                </Typography>
                            )}
                            <Box
                                role={ghost ? undefined : "img"}
                                aria-label={ghost ? undefined : `Week of ${weekLabel(week.weekStart)}: ${week.count} expiries`}
                                sx={{
                                    height,
                                    backgroundColor: ortexColor,
                                    opacity: active ? 1 : isCurrent ? 0.95 : 0.55,
                                    borderRadius: "4px 4px 0 0",
                                    transition: "opacity 0.12s ease",
                                }}
                            />
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ borderTop: `1px solid ${axisColor}`, display: "flex", gap: "2px", paddingTop: "4px" }}>
                {weeks.map((week, index) => (
                    <Box key={week.weekStart} sx={{ flex: 1, maxWidth: MAX_BAR_WIDTH, textAlign: "center", visibility: ghost ? "hidden" : undefined }}>
                        {labelled.has(index) && (
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.62rem", whiteSpace: "nowrap" }}>
                                {/* The placeholder's dates are hidden, and the server's locale need not be the reader's (#418). */}
                                {ghost ? "\u00a0" : index === 0 ? "This wk" : weekLabel(week.weekStart)}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>

            {/* Reserved rather than conditional, so hovering across the strip does not
                reflow the block beneath it. */}
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", minHeight: "1.2em", marginTop: "2px", visibility: ghost ? "hidden" : undefined }}>
                {hovered === null || ghost
                    ? `Next ${weeks.length} weeks`
                    : [
                          `Week of ${weekLabel(weeks[hovered].weekStart)}`,
                          `${weeks[hovered].count} ${weeks[hovered].count === 1 ? "expiry" : "expiries"}`,
                          weeks[hovered].valueUsd ? `$${nFormatter(weeks[hovered].valueUsd as number, 1)} unlocking` : null,
                      ]
                          .filter(Boolean)
                          .join(" · ")}
            </Typography>
        </Box>
    );
};

export default LockUpWeeks;
